/**
 * The thin strip tucked under the composer: the owner's sheet sentence and „Wrzuć arkusz" (board T), or,
 * while a dictation has something to say, its one line of status and its one act (board notes,
 * „States"). Same box either way, so its height never changes: a dictation's line is one line, cut
 * with an ellipsis where the width runs out (the whole sentence is its title and the live region's).
 * Each new line fades in; the countdown's seconds change in place.
 */

import clsx from 'clsx';
import type { FocusEvent } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import type { DictationLine } from '../model/dictation';
import { ErrorGlyph, HearGlyph, SheetGlyph } from './ChatGlyphs';
import styles from './Composer.module.css';

const ACTION_COPY = {
  undo: 'web:aiPlan.dictation.action.undo',
  again: 'web:aiPlan.dictation.action.again',
  retry: 'web:aiPlan.dictation.action.retry',
  tryAgain: 'web:aiPlan.dictation.action.tryAgain',
} as const;

export interface ComposerTrayProps {
  readonly line: DictationLine | null;
  /** Opens the spreadsheet import; `null` where none is offered. */
  readonly onSheet: (() => void) | null;
  readonly onAct: (action: NonNullable<DictationLine['action']>) => void;
  /** Whether focus is inside the tray, so a line that leaves by itself waits for it. */
  readonly onHold: (held: boolean) => void;
}

export function ComposerTray({ line, onSheet, onAct, onHold }: ComposerTrayProps) {
  const { t } = useTranslation();
  // Keyed by which sentence it is, so a new line enters; the sheet sentence on the first paint does not.
  const key = line === null ? 'sheet' : line.key;
  return (
    <p
      className={styles.tray}
      data-testid="composer-tray"
      onFocus={() => {
        onHold(true);
      }}
      onBlur={(event: FocusEvent<HTMLParagraphElement>) => {
        if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
        onHold(false);
      }}
    >
      <span key={key} className={clsx(styles.trayLine, line !== null && styles.trayLineEnters)}>
        {line === null ? (
          <>
            <span className={styles.trayText}>{t('web:aiPlan.composer.sheetOffer')}</span>
            {onSheet === null ? (
              <span className={styles.traySoon} aria-hidden="true">
                {t('web:aiPlan.composer.sheetSoon')}
              </span>
            ) : (
              <button type="button" className={styles.trayAction} onClick={onSheet}>
                <SheetGlyph className={styles.trayActionGlyph} />
                {t('web:aiPlan.composer.sheetAction')}
              </button>
            )}
          </>
        ) : (
          <>
            {line.glyph !== null &&
              (line.glyph.kind === 'hear' ? (
                <HearGlyph className={styles.trayGlyph} />
              ) : (
                <ErrorGlyph className={clsx(styles.trayGlyph, line.glyph.danger && styles.trayGlyphDanger)} />
              ))}
            <span className={clsx(styles.trayText, styles.trayOneLine, line.tone === 'strong' && styles.trayStrong)} title={t(line.key, line.values).replace(/<\/?k>/g, '')}>
              <Trans i18nKey={line.key} values={line.values} components={{ k: <kbd /> }} />
            </span>
            {line.action !== null && (
              <button
                type="button"
                className={styles.trayAction}
                onClick={() => {
                  if (line.action !== null) onAct(line.action);
                }}
              >
                {t(ACTION_COPY[line.action])}
              </button>
            )}
          </>
        )}
    </span>
  </p>
);
}
